import { useState } from "react";
import { Crown } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { api, formatApiError } from "@/lib/api";
import { useWorld } from "@/context/WorldContext";
import { ROLE_LABEL } from "@/lib/perms";

export const TransferOwnership = ({ team, worldId, reload }) => {
  const { world } = useWorld();
  const [to, setTo] = useState("");
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  if (team.my_role !== "owner") return null;
  const target = team.members.find((m) => m.user_id === to);
  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/worlds/${worldId}/transfer`, { user_id: to, confirm_name: name });
      toast.success(`${target.name || target.email} now owns ${world.name}. You're an Admin.`);
      if (!data.owner_pro) toast.warning("The new owner isn't on Pro, so editing by the team is paused until they upgrade.");
      setTo(""); setName(""); reload();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };
  return (
    <div className="rounded-xl border border-rose-500/30 bg-rose-500/[0.03] p-4 space-y-3" data-testid="transfer-ownership">
      <div>
        <p className="text-sm text-slate-200 flex items-center gap-2"><Crown className="w-4 h-4 text-amber-400" /> Transfer ownership</p>
        <p className="text-[11px] text-slate-500 mt-1">Hand this world to a member. They get billing and full control, and you stay on as an Admin. You can't undo this yourself.</p>
      </div>
      {team.members.length === 0 ? <p className="text-xs text-slate-500">Invite someone first. Only current members can take over.</p> : (
        <>
          <select value={to} onChange={(e) => setTo(e.target.value)} className="h-9 w-full rounded-md bg-inset border border-line text-xs text-slate-200 px-2" data-testid="transfer-member-select">
            <option value="">Choose the new owner</option>
            {team.members.map((m) => <option key={m.user_id} value={m.user_id}>{m.name || m.email} · {ROLE_LABEL[m.role]}</option>)}
          </select>
          {to && (
            <>
              <p className="text-[11px] text-slate-400">Type <span className="text-amber-200 font-code">{world.name}</span> to confirm.</p>
              <Input value={name} onChange={(e) => setName(e.target.value)} className="h-9" placeholder={world.name} data-testid="transfer-confirm-input" />
              <button disabled={busy || name.trim() !== world.name.trim()} onClick={submit}
                className="w-full rounded-full border border-rose-500/50 text-rose-200 hover:bg-rose-500/10 disabled:opacity-40 text-xs font-semibold h-9 transition-colors" data-testid="transfer-submit-btn">
                Make {target?.name || target?.email} the owner
              </button>
            </>
          )}
        </>
      )}
    </div>
  );
};
